import { cn } from '@quadralize/utils'
import { type HTMLMotionProps, motion } from 'framer-motion'
import * as React from 'react'

// Context to share configuration like position to items
type TimelineContextValue = {
  position?: 'left' | 'right' | 'alternate'
}
const TimelineContext = React.createContext<TimelineContextValue>({ position: 'left' })

const Timeline = React.forwardRef<
  HTMLUListElement,
  HTMLMotionProps<'ul'> & { position?: 'left' | 'right' | 'alternate' }
>(({ className, position = 'left', children, ...props }, ref) => (
  <TimelineContext.Provider value={{ position }}>
    <motion.ul
      initial="hidden"
      animate="show"
      variants={{
        hidden: { opacity: 0 },
        show: {
          opacity: 1,
          transition: { staggerChildren: 0.1 },
        },
      }}
      ref={ref}
      className={cn('flex flex-col p-0 m-0 list-none w-full', className)}
      {...props}
    >
      {children}
    </motion.ul>
  </TimelineContext.Provider>
))
Timeline.displayName = 'Timeline'

const TimelineItem = React.forwardRef<HTMLLIElement, HTMLMotionProps<'li'>>(
  ({ className, ...props }, ref) => {
    const { position } = React.useContext(TimelineContext)

    return (
      <motion.li
        variants={{
          hidden: { opacity: 0, y: 10 },
          show: { opacity: 1, y: 0 },
        }}
        ref={ref}
        className={cn(
          'relative flex min-h-[70px]',
          // Alternate logic: Even items reversed
          position === 'alternate' && 'even:flex-row-reverse group',
          position === 'right' && 'flex-row-reverse',
          className,
        )}
        {...props}
      />
    )
  },
)
TimelineItem.displayName = 'TimelineItem'

const TimelineSeparator = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col items-center flex-none', className)} {...props} />
  ),
)
TimelineSeparator.displayName = 'TimelineSeparator'

const TimelineConnector = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { variant?: 'solid' | 'dashed' }
>(({ className, variant = 'solid', ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'w-[2px] grow bg-border-default my-2',
      variant === 'dashed' && 'bg-transparent border-l-2 border-dashed border-border-default w-0',
      className,
    )}
    {...props}
  />
))
TimelineConnector.displayName = 'TimelineConnector'

const TimelineDot = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    variant?: 'default' | 'outline' | 'ghost'
    size?: 'sm' | 'default' | 'lg'
  }
>(({ className, variant = 'default', size = 'default', ...props }, ref) => {
  const variantStyles = {
    default: 'bg-primary text-primary-fg border border-primary',
    outline: 'border-2 border-primary bg-bg-base text-primary',
    ghost: 'bg-transparent text-text-secondary',
  }

  const sizeStyles = {
    sm: 'w-3 h-3 p-0.5',
    default: 'w-9 h-9 p-2', // 36px - good for icons
    lg: 'w-11 h-11 p-2.5',
  }

  return (
    <div
      ref={ref}
      className={cn(
        'flex items-center justify-center rounded-full z-10',
        variant !== 'ghost' && 'shadow-sm',
        variantStyles[variant],
        sizeStyles[size],
        className,
      )}
      {...props}
    />
  )
})
TimelineDot.displayName = 'TimelineDot'

const TimelineContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { position } = React.useContext(TimelineContext)
    return (
      <div
        ref={ref}
        className={cn(
          'flex-1 pt-0.5 pb-8 pl-4 text-sm text-text-primary',
          // Adjust padding/text alignment based on position
          position === 'alternate' && 'group-even:text-right group-even:pl-0 group-even:pr-4',
          position === 'right' && 'text-right pl-0 pr-4',
          className,
        )}
        {...props}
      />
    )
  },
)
TimelineContent.displayName = 'TimelineContent'

const TimelineOppositeContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { position } = React.useContext(TimelineContext)
  return (
    <div
      ref={ref}
      className={cn(
        'flex-1 pt-0.5 pb-8 pr-4 text-right text-sm text-text-tertiary',
        // Adjust padding/text alignment based on position
        position === 'alternate' && 'group-even:text-left group-even:pr-0 group-even:pl-4',
        position === 'right' && 'text-left pr-0 pl-4',
        className,
      )}
      {...props}
    />
  )
})
TimelineOppositeContent.displayName = 'TimelineOppositeContent'

export {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
}
